import {
    featureId,
    LANGUAGE_FOR_EMAIL_PROPERTY_NAME,
    QUOTED_TEXT_LANGUAGES_PROPERTY_NAME,
} from '../utils/inlineTranslationLocaleItemPropertyEntry';
import { isFeatureEnabled } from 'owa-feature-flags';
import { lazyGetItemProperties } from 'owa-get-item-manager';
import { ClientItem } from 'owa-mail-store';
import { getExtendedProperty } from 'owa-mail-store/lib/utils/getExtendedPropertyValue';
import Item from 'owa-service/lib/contract/Item';
import { trace } from 'owa-trace';

const MINIMUM_ITEM_TEXT_LENGTH = 15;
const SHORT_ITEM_TEXT_LENGTH = 40;
const LANGUAGE_KEY = 'Language';
const LANGUAGES_KEY = 'Languages';
const LOCALE_KEY = 'Locale';
const BEST_GUESS_LANGUAGE_KEY = 'BestGuessLanguage';
const FAST_TEXT_LANGUAGE_KEY = 'fastTextLanguage';

export default async function getLocalePropertyForItem(item: ClientItem): Promise<string[]> {
    // Encrypted items are not supposed to have language stamped.
    // But these (along with all right protected) items are always stamped with 'en-US'.
    // We need to ignore these items. Language property is not supposed to be loaded for
    // this item - but SmartRelply feature is loading it. Although the property has been
    // loaded - we are ignoring it here.
    if (item.RightsManagementLicenseData) {
        return ['', null];
    }

    let updatedItem: Item = null;
    try {
        const getItemProperties = await lazyGetItemProperties.import();
        updatedItem = await getItemProperties(item.ItemId.Id, featureId);
    } catch (e) {
        // There is an error when trying to get item properties from server.
        trace.info('getLocalePropertyForItem: Unable to get item property from GetItemManager.');
    }

    let language = '';
    let quotedTextLanguages = null;
    if (updatedItem) {
        const firstBodyLanguageProperty = getExtendedProperty(
            updatedItem,
            null /*tag*/,
            LANGUAGE_FOR_EMAIL_PROPERTY_NAME
        );
        const quotedTextLanguagesProperty = getExtendedProperty(
            updatedItem,
            null /*tag*/,
            QUOTED_TEXT_LANGUAGES_PROPERTY_NAME
        );

        if (firstBodyLanguageProperty) {
            // extended property is present, parse and return the locale
            const parsedProperty = JSON.parse(firstBodyLanguageProperty.Value)[0];
            const languageDetails = parsedProperty[LANGUAGE_KEY];
            if (languageDetails) {
                // Items that are too small, we don't even consider for trnaslation.
                // Items that are a little longer but still short, we only consider
                // if BestGuessLanguage property is present.
                if (item.Preview.length < MINIMUM_ITEM_TEXT_LENGTH) {
                } else if (item.Preview.length < SHORT_ITEM_TEXT_LENGTH) {
                    // For short messages we get language from best guess language
                    // field or FastText language only if feature is enabled.
                    if (isFeatureEnabled('tra-EnableShortMessage')) {
                        if (
                            isFeatureEnabled('tra-useFastTextLanguage') &&
                            languageDetails.hasOwnProperty(FAST_TEXT_LANGUAGE_KEY)
                        ) {
                            item.TranslationData.isFastTextLanguage = true;
                            language = languageDetails[FAST_TEXT_LANGUAGE_KEY];
                        } else if (languageDetails.hasOwnProperty(BEST_GUESS_LANGUAGE_KEY)) {
                            item.TranslationData.isFastTextLanguage = false;
                            language = languageDetails[BEST_GUESS_LANGUAGE_KEY];
                        }
                    }
                } else if (
                    isFeatureEnabled('tra-useFastTextLanguage') &&
                    languageDetails.hasOwnProperty(FAST_TEXT_LANGUAGE_KEY)
                ) {
                    item.TranslationData.isFastTextLanguage = true;
                    language = languageDetails[FAST_TEXT_LANGUAGE_KEY];
                } else {
                    item.TranslationData.isFastTextLanguage = false;
                    language = languageDetails.hasOwnProperty(BEST_GUESS_LANGUAGE_KEY)
                        ? languageDetails[BEST_GUESS_LANGUAGE_KEY]
                        : languageDetails[LOCALE_KEY];
                }
            }
        } else {
            trace.info(
                'getLocalePropertyForItem: No language property found for first body of item.'
            );
        }

        if (quotedTextLanguagesProperty) {
            const parsedProperty = JSON.parse(quotedTextLanguagesProperty.Value)[0];
            quotedTextLanguages = parsedProperty[LANGUAGES_KEY];
        }
    }

    return [language || '', quotedTextLanguages];
}
